import { APPLICATION_CATALOG } from './packages';
import { precioDeLaFila } from './precioDeAplicacion.js';

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function quoteObject(quote = {}) {
  return safeObject(quote);
}

function clean(value, fallback = '') {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function normalizeText(value) {
  return clean(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function slugify(value) {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'aplicacion';
}

// UNA SOLA AUTORIDAD DE PRECIO DE APLICACION. Su gemelo de borde es
// `supabase/functions/_shared/precio-de-aplicacion.ts`, y la paridad se certifica
// EJECUTANDO los dos contra la misma tabla de casos.
function numeric(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

// EL PRECIO DE UNA APLICACION SALE DE UNA SOLA AUTORIDAD.
//
// Antes esto era `numeric(..., 0)`: un precio nulo salia 0, y para una aplicacion que
// REQUIERE COTIZACION ese cero la volvia gratis. «Gratis» es una afirmacion comercial
// que nadie tomo. Su gemelo exacto vivia en `_shared/application-scope.ts:46`.
//
// El mercado es NACIONAL porque esto lee el importe ESCRITO en la fila (`base_price`),
// no el par internacional: cuando este dato llega aqui el mercado ya se resolvio
// aguas arriba, en la cotizacion.
export function veredictoDePrecioDeAplicacion(app = {}) {
  const item = safeObject(app);
  const id = clean(item.id || item.application_id || item.slug) || clean(item.name) || 'x';
  return precioDeLaFila({
    ...item,
    // `extra_price` es el importe de una linea de seleccion; para la autoridad es el
    // mismo campo de precio, no una fuente distinta.
    base_price: item.base_price ?? item.basePrice ?? item.price ?? item.precio ?? item.extra_price,
  }, { applicationId: id, mercado: 'nacional' });
}

// `null` cuando no resuelve. NUNCA 0: un cero aqui es una decision comercial.
function appPrice(app = {}) {
  return veredictoDePrecioDeAplicacion(app).precio;
}

export function normalizeApplicationCatalog(catalog = APPLICATION_CATALOG) {
  const seen = new Set();
  return safeArray(catalog).map((app) => {
    const item = safeObject(app);
    const name = clean(item.name || item.nombre || item.label || item.id, 'Aplicacion');
    const id = clean(item.id || item.application_id || item.slug, slugify(name));
    if (!id || seen.has(id)) return null;
    seen.add(id);
    return {
      ...item,
      id,
      application_id: id,
      name,
      nombre: clean(item.nombre, name),
      category: clean(item.category || item.categoria, 'Aplicacion'),
      categoria: clean(item.categoria || item.category, 'Aplicacion'),
      basePrice: appPrice(item),
      base_price: appPrice(item),
      visible: item.visible !== false,
      status: clean(item.status || item.estado, 'active'),
    };
  }).filter(Boolean);
}

export function applicationByIdFromCatalog(id, catalog = APPLICATION_CATALOG) {
  const normalized = normalizeApplicationCatalog(catalog);
  const target = clean(id);
  const direct = normalized.find(app => clean(app.id) === target || clean(app.application_id) === target);
  if (direct) return direct;
  const byName = normalized.find(app => slugify(app.name) === target || normalizeText(app.name) === normalizeText(target));
  return byName || null;
}

function quoteItems(quote = {}) {
  return safeObject(quoteObject(quote).items);
}

function quotePayload(items = {}) {
  return safeObject(items.quote_payload || items.quotePayload);
}

function quoteBlock(items = {}) {
  const payload = quotePayload(items);
  return safeObject(items.quote_block || items.quoteBlock || payload.quote_block || payload.quoteBlock);
}

function packageSnapshots(items = {}) {
  const payload = quotePayload(items);
  const rows = safeArray(items.package_snapshots).length
    ? safeArray(items.package_snapshots)
    : safeArray(items.packages).length
      ? safeArray(items.packages)
      : safeArray(payload.package_snapshots).length
        ? safeArray(payload.package_snapshots)
        : safeArray(payload.packages);
  return rows.map(row => safeObject(row)).filter(row => clean(row.id || row.nombre || row.name));
}

function clientSelection(quote = {}) {
  const item = quoteObject(quote);
  return safeObject(item.client_selection || item.clientSelection);
}

function hasSelectionPayload(selection = {}) {
  return Object.keys(safeObject(selection)).length > 0;
}

function selectedPackageId(quote = {}, selection = {}) {
  const item = quoteObject(quote);
  const selected = safeObject(selection);
  const pkg = safeObject(selected.package);
  return clean(
    item.final_package_id ||
    selected.package_id ||
    selected.packageId ||
    pkg.id ||
    item.paquete_id ||
    ''
  );
}

function findPackageSnapshot(quote = {}, selection = {}) {
  const items = quoteItems(quote);
  const snapshots = packageSnapshots(items);
  const packageId = selectedPackageId(quote, selection);
  const selectedPackage = safeObject(safeObject(selection).package);
  return snapshots.find(pkg => clean(pkg.id) === packageId)
    || snapshots.find(pkg => normalizeText(pkg.nombre || pkg.name) === normalizeText(selectedPackage.name))
    || {};
}

function addApp(map, app, source, overrides = {}) {
  const item = safeObject(app);
  const name = clean(item.name || item.nombre || item.label || item.id, 'Aplicacion');
  const id = clean(item.id || item.application_id || item.slug, slugify(name));
  if (!id) return;
  const existing = map.get(id) || {};
  map.set(id, {
    ...existing,
    ...item,
    ...overrides,
    id,
    application_id: id,
    name,
    nombre: clean(item.nombre, name),
    category: clean(item.category || item.categoria || existing.category, 'Aplicacion'),
    categoria: clean(item.categoria || item.category || existing.categoria, 'Aplicacion'),
    // EL CERO CONTRACTUAL SE RESPETA.
    //
    // Estas dos lineas iban DESPUES de `...overrides`, asi que pisaban el
    // `{ basePrice: 0 }` que piden las dos llamadas de inclusion. Medido ejecutando
    // `addApp`: una aplicacion de catalogo a 220 que el paquete INCLUYE salia a 220.
    //
    // No era una fuga viva -quien cobra es `ClientPortal`, y alli decide `isExtra`, no
    // el precio- pero el override estaba escrito, era intencionado y no hacia nada.
    basePrice: overrides.basePrice !== undefined ? overrides.basePrice : appPrice(item),
    base_price: overrides.base_price !== undefined ? overrides.base_price : appPrice(item),
    source,
  });
}

function matchAppFromName(name, catalog = []) {
  const target = normalizeText(name);
  return normalizeApplicationCatalog(catalog).find(app => normalizeText(app.name) === target || slugify(app.name) === slugify(name));
}

// ── EL SERVICIO DECIDE SI HAY ETAPA DE APLICACIONES ────────────────────────
//
// MEDIDO el 2026-09-10 con una venta WEB real: el snapshot del paquete web
// lleva en `items` sus LINEAS DE ALCANCE («Objetivo del sitio: …», «Alcance:
// …», «Forma de pago: …»), y este constructor las convertia una a una en
// «aplicaciones»: diez pseudo-aplicaciones que llegaban al brief («10
// aplicaciones incluidas»), al contrato y al proyecto (`package_app_limit = 10`
// y diez filas de `proyecto_aplicaciones`). Un sitio web no tiene aplicaciones
// de marca: tiene alcance, y opcionalmente servicios adicionales.
//
// La autoridad es `items.service_type`, la misma que decide la presentacion
// publica y el brief. Solo `web` deja de tener etapa de aplicaciones; branding
// y los demas siguen exactamente como estaban.
export function servicioDeLaCotizacion(quote = {}) {
  const quoteRow = quoteObject(quote);
  const items = quoteItems(quoteRow);
  const snapshot = findPackageSnapshot(quoteRow, clientSelection(quoteRow));
  return clean(items.service_type || items.serviceType || items.service_kind || snapshot.family || snapshot.categoria)
    .toLowerCase();
}

export function servicioUsaAplicaciones(serviceType = '') {
  return clean(serviceType).toLowerCase() !== 'web';
}

export function buildAcceptedApplicationScope(quote = {}, options = {}) {
  const quoteRow = quoteObject(quote);
  const fallbackCatalog = normalizeApplicationCatalog(options.fallbackCatalog || APPLICATION_CATALOG);
  const items = quoteItems(quoteRow);
  const selection = clientSelection(quoteRow);
  const pricing = safeObject(selection.pricing);
  const fallbackPackageName = clean(options.fallbackPackageName || quoteRow.paquete_nombre || quoteRow.package_name, 'Paquete');
  const selectionStatus = clean(quoteRow.client_selection_status || selection.status);
  const hasAcceptedSelection = hasSelectionPayload(selection)
    && Boolean(selection.package || quoteRow.final_package_id || selectionStatus === 'accepted');
  const serviceType = servicioDeLaCotizacion(quoteRow);
  const usesApplicationStage = servicioUsaAplicaciones(serviceType);

  if (!hasAcceptedSelection) {
    return {
      hasAcceptedQuoteScope: false,
      source: 'legacy',
      serviceType,
      usesApplicationStage,
      scopeLines: [],
      packageId: '',
      packageName: fallbackPackageName,
      packagePrice: 0,
      currency: 'USD',
      includedApplicationLimit: 0,
      acceptedApplicationIds: [],
      includedApplicationIds: [],
      quoteAdditionalApplicationIds: [],
      acceptedApplications: [],
      includedApplications: [],
      quoteAdditionalApplications: [],
      applicationCatalog: fallbackCatalog,
    };
  }

  const block = quoteBlock(items);
  const selectedPackage = safeObject(selection.package);
  const snapshot = findPackageSnapshot(quoteRow, selection);
  // Las lineas del paquete aceptado, tal cual: para web son el ALCANCE y se
  // muestran como tal; para branding son nombres de aplicaciones y siguen
  // entrando por el bucle de abajo.
  const scopeLines = safeArray(snapshot.items).map(line => clean(typeof line === 'string' ? line : line?.name || line?.nombre)).filter(Boolean);
  const packageId = selectedPackageId(quoteRow, selection) || clean(snapshot.id);
  const packageName = clean(selectedPackage.name || snapshot.nombre || snapshot.name || quoteRow.paquete_nombre || fallbackPackageName, 'Paquete aceptado');
  const packagePrice = numeric(selectedPackage.price ?? pricing.package_price ?? snapshot.precio ?? snapshot.price ?? quoteRow.final_total, 0);
  const currency = clean(selectedPackage.currency || pricing.currency || quoteRow.final_currency, 'USD');
  const appMap = new Map(fallbackCatalog.map(app => [app.id, app]));
  const includedMap = new Map();
  const additionalMap = new Map();

  safeArray(block.optional_applications).forEach((app) => {
    const item = safeObject(app);
    const includedPackages = safeArray(item.included_in_package_ids).map(id => clean(id)).filter(Boolean);
    if (packageId && includedPackages.includes(packageId)) {
      addApp(includedMap, item, 'quote_package_relation', { basePrice: 0, base_price: 0 });
      addApp(appMap, item, 'quote_block');
    } else {
      addApp(appMap, item, 'quote_block');
    }
  });

  // Solo un servicio CON etapa de aplicaciones convierte las lineas del paquete
  // en aplicaciones. Para web esas lineas son alcance (`scopeLines`).
  if (usesApplicationStage) {
    safeArray(snapshot.items).forEach((name, index) => {
      const matched = matchAppFromName(name, fallbackCatalog);
      addApp(includedMap, matched || { id: slugify(name), name, category: 'Aplicacion', basePrice: 0 }, 'quote_package_snapshot', {
        basePrice: 0,
        base_price: 0,
        sort_order: index + 1,
      });
    });
  }

  safeArray(selection.additional_items || selection.additionalItems).forEach((app, index) => {
    addApp(additionalMap, app, 'quote_selection_addon', {
      basePrice: appPrice(app),
      base_price: appPrice(app),
      sort_order: index + 1,
    });
    addApp(appMap, app, 'quote_selection_addon');
  });

  const includedApplications = Array.from(includedMap.values());
  const quoteAdditionalApplications = Array.from(additionalMap.values());
  // Sin etapa de aplicaciones no hay «aplicaciones aceptadas»: los servicios
  // opcionales que la clienta sumo se conservan aparte
  // (`quoteAdditionalApplications`) y no se vuelven a elegir despues del brief.
  const acceptedApplications = usesApplicationStage
    ? [...includedApplications, ...quoteAdditionalApplications]
      .filter((app, index, rows) => rows.findIndex(row => row.id === app.id) === index)
    : [];
  const acceptedApplicationIds = acceptedApplications.map(app => app.id);

  return {
    hasAcceptedQuoteScope: true,
    source: 'quote_selection',
    serviceType,
    usesApplicationStage,
    scopeLines,
    quoteId: quoteRow.id || '',
    packageId,
    packageName,
    packagePrice,
    currency,
    includedApplicationLimit: acceptedApplications.length,
    acceptedApplicationIds,
    includedApplicationIds: includedApplications.map(app => app.id),
    quoteAdditionalApplicationIds: quoteAdditionalApplications.map(app => app.id),
    acceptedApplications,
    includedApplications,
    quoteAdditionalApplications,
    applicationCatalog: Array.from(appMap.values()).filter(app => app.visible !== false),
  };
}

export function isApplicationInAcceptedScope(scope = {}, applicationId = '') {
  return safeArray(scope.acceptedApplicationIds).includes(clean(applicationId));
}

export function buildApplicationRowsFromScope(scope = {}) {
  return safeArray(scope.acceptedApplications).map((app, index) => ({
    application_id: app.id,
    id: app.id,
    name: app.name,
    category: app.category,
    status: 'pendiente',
    is_extra: false,
    extra_price: 0,
    extra_status: 'incluida_en_cotizacion',
    selected_by: 'cliente',
    sort_order: index + 1,
    source: scope.source || 'quote_selection',
  }));
}
